import { useState, useCallback } from 'react';
import api from '../api/axios';

/* ========= Tipos de dominio ========= */
export type FormatoStr = 'presencial' | 'online' | 'a_domicilio';

/** Fila que devuelve el backend en el GET (listado semanal) */
export interface DisponibilidadFilaRead {
  id: number;
  idProfesor: number;
  diaSemana: number; // 0..6 (lunes..domingo)
  idCentro?: number | null;
  horaInicio: string; // HH:mm:ss
  horaFin: string; // HH:mm:ss
  comentario?: string | null;
  formatosMask?: number; // lectura
  vigenciaDesde?: string | null; // YYYY-MM-DD
  vigenciaHasta?: string | null; // YYYY-MM-DD | null
}

/** Fila que espera el backend en el REPLACE (PUT) */
export interface ReplaceFilaDto {
  idProfesor: number;
  diaSemana: number; // 0..6
  horaInicio: string; // HH:mm
  horaFin: string; // HH:mm
  comentario?: string; // opcional
  formatos: FormatoStr[]; // requerido
  /** Si no se informa => centro global (dinámico) */
  centros?: number[];
}

/** Payload del REPLACE */
export interface ReplacePayload {
  vigenciaDesde: string; // YYYY-MM-DD
  vigenciaHasta?: string; // opcional
  sourceVigenciaDesde?: string; // vigencia origen (edición)
  sourceVigenciaHasta?: string | null; // vigencia origen (edición)
  filas: ReplaceFilaDto[];
}

export function useDisponibilidad(idProfesor: number) {
  const [rows, setRows] = useState<DisponibilidadFilaRead[]>([]);
  const [loading, setL] = useState(false);

  /* -------- READ -------- */
  const refresh = useCallback(async (): Promise<DisponibilidadFilaRead[]> => {
    if (!idProfesor) return [];
    setL(true);
    try {
      const { data } = await api.get<DisponibilidadFilaRead[]>(
        `/disponibilidad-profesor?profesor=${idProfesor}`,
      );
      setRows(data ?? []);
      return data ?? [];
    } finally {
      setL(false);
    }
  }, [idProfesor]);

  /**
   * -------- REPLACE ALL --------
   * Envía { vigenciaDesde, vigenciaHasta?, sourceVigenciaDesde?, sourceVigenciaHasta?, filas }
   * con horas HH:mm y formatos: string[]
   */
  const saveAll = useCallback(
    (
      filas: ReplaceFilaDto[],
      vigenciaDesde: string,
      vigenciaHasta?: string,
      sourceVigenciaDesde?: string,
      sourceVigenciaHasta?: string | null,
    ) =>
      api
        .put(`/disponibilidad-profesor/profesor/${idProfesor}/replace`, {
          vigenciaDesde,
          ...(vigenciaHasta ? { vigenciaHasta } : {}),
          ...(sourceVigenciaDesde ? { sourceVigenciaDesde } : {}),
          ...(sourceVigenciaHasta !== undefined ? { sourceVigenciaHasta } : {}),
          filas,
        } as ReplacePayload)
        .then(() => refresh()),
    [idProfesor, refresh],
  );

  return { rows, loading, refresh, saveAll };
}
